interface Tab<T extends string> {
  id: T;
  label: string;
}

interface Props<T extends string> {
  tabs: readonly Tab<T>[];
  active: T;
  onChange: (id: T) => void;
}

/**
 * Segmented-control style tab bar. Sticky placement is the parent's job; this
 * component is just the pill row. Designed to drop into the dashboard header
 * area without competing visually with the existing RefreshControl.
 */
export function Tabs<T extends string>({ tabs, active, onChange }: Props<T>) {
  return (
    <div
      role="tablist"
      className="inline-flex w-full sm:w-auto rounded-lg bg-slate-100 dark:bg-slate-800/70 p-0.5 text-sm shadow-inner overflow-x-auto"
    >
      {tabs.map((tab) => {
        const isActive = tab.id === active;
        return (
          <button
            key={tab.id}
            role="tab"
            aria-selected={isActive}
            type="button"
            onClick={() => onChange(tab.id)}
            className={
              "shrink-0 flex-1 sm:flex-initial px-3 sm:px-4 py-1.5 rounded-md font-medium transition whitespace-nowrap " +
              (isActive
                ? "bg-white dark:bg-slate-950 text-slate-900 dark:text-white shadow-sm ring-1 ring-slate-200 dark:ring-slate-700"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200")
            }
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
